<template>
    <v-sheet v-if="alert" class="pa-3" :class="sheetClass"
        :color="alertStatus === 0 ? (isUrgent ? 'red lighten-5' : 'orange lighten-5') : 'grey lighten-4'">
        <template v-if="alertStatus === 0">
            <div class="d-flex align-center flex-wrap" style="gap: 4px;">
                <v-icon small class="mr-1" :color="isUrgent ? 'red darken-2' : 'orange darken-2'">
                    mdi-shield-alert
                </v-icon>
                <v-chip v-for="c in alert.categories" :key="c.type" x-small dark
                    :color="severityColor(c.severity)">
                    {{ $t(`safety_alert.category.${c.type}`) }} {{ c.severity }}
                </v-chip>
            </div>
            <div v-for="msg in alert.messages" :key="msg.safety_alert_id"
                class="text-caption mt-1 text-truncate" :title="msg.content">
                「{{ msg.content }}」
            </div>
            <div class="d-flex align-center mt-2" style="gap: 8px;">
                <v-btn small outlined color="red darken-2" class="normal-case" :loading="updating"
                    @click="openConfirm(1)">
                    {{ $t('safety_alert.action.report') }}
                </v-btn>
                <v-btn small outlined class="normal-case" :loading="updating" @click="openConfirm(2)">
                    {{ $t('safety_alert.action.ignore') }}
                </v-btn>
            </div>
            <!-- 標記確認框:記錄處理人且不可還原,操作前確認 -->
            <v-dialog v-model="confirmDialog" width="420">
                <v-card class="pb-2">
                    <v-card-title class="subtitle-1">
                        {{ confirmTitle }}
                    </v-card-title>
                    <v-card-text>
                        {{ $t('safety_alert.confirm.description') }}
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text small class="normal-case" @click="confirmDialog = false">
                            {{ $t('button.cancel') }}
                        </v-btn>
                        <v-btn color="red darken-2" dark small class="normal-case" @click="confirmUpdate">
                            {{ $t('button.confirm') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </template>
        <!-- 已處理:降噪成一行狀態列(狀態用文字+icon,避免與按鈕混淆;還原功能已自前端移除,API 仍支援 status=0) -->
        <div v-else class="d-flex align-center flex-wrap" style="gap: 6px;">
            <span class="text-caption font-weight-medium d-inline-flex align-center" style="white-space: nowrap;"
                :class="alertStatus === 1 ? 'green--text text--darken-2' : 'grey--text text--darken-1'">
                <v-icon small class="mr-1" :color="alertStatus === 1 ? 'green darken-2' : 'grey darken-1'">
                    mdi-check-circle-outline
                </v-icon>
                {{ alertStatus === 1 ? $t('safety_alert.status.reported') : $t('safety_alert.status.ignored') }}
            </span>
            <v-chip v-for="c in alert.categories" :key="c.type" x-small outlined color="grey darken-1">
                {{ $t(`safety_alert.category.${c.type}`) }} {{ c.severity }}
            </v-chip>
            <span v-if="alert.handled_by_name" class="text-caption grey--text">
                {{ handledInfo }}
            </span>
        </div>
    </v-sheet>
</template>

<script>
import { safetyAlertServices } from '@/services/safetyAlert.js';
import dayjs from 'dayjs';

/**
 * 安全警示區塊(共用):顯示類別 chips、觸發句與處理操作。
 * 用於 PostDialog 頂部與 News 動態牆的日記卡片上方。
 * alert 為 null/undefined 時不渲染。
 * 操作成功 → $emit('updated', status) + 廣播 safety-summary-refresh;失敗 → $emit('update-failed')。
 */
export default {
    name: 'SafetyAlertBlock',
    props: ['alert', 'sheetClass'],
    data() {
        return {
            alertStatus: null,
            updating: false,
            confirmDialog: false,
            confirmTargetStatus: null,
        };
    },
    watch: {
        alert: {
            immediate: true,
            handler(val) {
                this.alertStatus = val ? val.status : null;
            },
        },
    },
    computed: {
        isUrgent() {
            return (this.alert?.highest_severity ?? 0) >= 3;
        },
        confirmTitle() {
            if (this.confirmTargetStatus == null) return '';
            const statusText = this.confirmTargetStatus === 1
                ? this.$t('safety_alert.status.reported')
                : this.$t('safety_alert.status.ignored');
            return this.$t('safety_alert.confirm.title').replace('${status}', statusText);
        },
        /** 標籤式句型:處理人：PJ（2026-07-09 20:51） */
        handledInfo() {
            if (!this.alert?.handled_by_name) return '';
            const time = this.alert.handled_at
                ? dayjs(this.alert.handled_at).format('YYYY-MM-DD HH:mm') : '-';
            return this.$t('safety_alert.handled_info')
                .replace('${name}', this.alert.handled_by_name)
                .replace('${time}', time);
        },
    },
    methods: {
        severityColor(severity) {
            if (severity >= 3) return 'red darken-2';
            if (severity === 2) return 'orange darken-2';
            return 'amber darken-1';
        },
        openConfirm(status) {
            this.confirmTargetStatus = status;
            this.confirmDialog = true;
        },
        async confirmUpdate() {
            this.confirmDialog = false;
            if (this.confirmTargetStatus == null) return;
            await this.updateAlert(this.confirmTargetStatus);
            this.confirmTargetStatus = null;
        },
        async updateAlert(status) {
            if (!this.alert) return;
            this.updating = true;
            try {
                await safetyAlertServices.updateByPost(this.alert.diary_post_id, status);
                this.alertStatus = status;
                // 警示條 / badge 同步
                this.$root.$emit('safety-summary-refresh');
                this.$emit('updated', status);
            } catch (err) {
                this.$emit('update-failed');
            }
            this.updating = false;
        },
    },
};
</script>
<style scoped></style>
